<template>
  <div class="settings-form-block">
    <div class="d-flex flex-row align-items-start">
      <div class="d-flex flex-column flex-fill">
        <slot name="title">
          <span class="lead">
            <span v-if="testEmail" @click.meta="showCronDetails" :class="isCronActive ? 'cron-on-switch' : 'cron-off-switch'"></span>{{ title }}
            <font-awesome-icon v-if="icon" class="ml-2 primary-color-semi-opaque" :icon="icon" :style="{ width: '24px' }"/>
          </span>
          <span>{{ description }}</span>
        </slot>
        <!-- slot name="description"><p>{{ description }}</p></slot -->
      </div>
      <b-button @click="() => visible = !visible" size="sm" variant="outline">
        <template v-if="visible">Close</template>
        <template v-else>Expand</template>
      </b-button>
    </div>
    <transition name="fade">
      <slot v-if="visible">
        <b-form @submit.prevent="onFormSubmit" @reset="onFormReset" class="mt-4">

          <slot name="before-form"/>

          <!-- if tabs -->
          <template v-if="tabbedFields">

            <!-- common fields -->
            <template v-for="(field, i) in tabbedFields['none']">
              <template-block v-if="field.type === 'template'" :key="i" :field="field" v-model="formData[field.key]"/>
              <score-block v-else-if="field.type === 'score'" :key="i" :field="field" v-model="formData[field.key]"/>
              <asset-block v-else-if="field.type === 'asset-image'" :key="i" :field="field" show-preview v-model="formData[field.key]"/>
              <asset-block v-else-if="field.type === 'asset-document'" :key="i" :field="field" v-model="formData[field.key]"/>
              <onboarding-block v-else-if="field.type === 'onboardingSteps'" :key="i" :field="field" v-model="formData[field.key]"/>
              <dashboard-block v-else-if="field.type === 'dashboardBlocks'" :key="i" :field="field" v-model="formData[field.key]"/>
              <input-block v-else :key="i" :field="field" v-model="formData[field.key]"/>
            </template>

            <!-- tabbed fields -->
            <b-card class="mb-3" no-body>
              <b-tabs lazy card>
                <b-tab v-for="tab in tabs" :title="tab.title" :key="tab.key">
                  <template v-for="(field, i) in tabbedFields[tab.key]">
                    <template-block v-if="field.type === 'template'" :key="`${i}${tab.key}`" :field="field" v-model="formData[field.key]"/>
                    <score-block v-else-if="field.type === 'score'" :key="`${i}${tab.key}`" :field="field" v-model="formData[field.key]"/>
                    <asset-block v-else-if="field.type === 'asset-image'" :key="`${i}${tab.key}`" :field="field" show-preview v-model="formData[field.key]"/>
                    <asset-block v-else-if="field.type === 'asset-document'" :key="`${i}${tab.key}`" :field="field" v-model="formData[field.key]"/>
                    <onboarding-block v-else-if="field.type === 'onboardingSteps'" :key="`${i}${tab.key}`" :field="field" v-model="formData[field.key]"/>
                    <dashboard-block v-else-if="field.type === 'dashboardBlocks'" :key="`${i}${tab.key}`" :field="field" v-model="formData[field.key]"/>
                    <input-block v-else :key="`${i}${tab.key}`" :field="field" v-model="formData[field.key]"/>
                  </template>
                </b-tab>
              </b-tabs>
            </b-card>

          </template>

          <!-- if no tabs -->
          <template v-else v-for="(field, i) in fields">
            <template-block v-if="field.type === 'template'" :key="i" :field="field" v-model="formData[field.key]"/>
            <score-block v-else-if="field.type === 'score'" :key="i" :field="field" v-model="formData[field.key]"/>
            <asset-block v-else-if="field.type === 'asset-image'" :key="i" :field="field" show-preview v-model="formData[field.key]"/>
            <asset-block v-else-if="field.type === 'asset-document'" :key="i" :field="field" v-model="formData[field.key]"/>
            <onboarding-block v-else-if="field.type === 'onboardingSteps'" :key="i" :field="field" v-model="formData[field.key]"/>
            <dashboard-block v-else-if="field.type === 'dashboardBlocks'" :key="i" :field="field" v-model="formData[field.key]"/>
            <input-block v-else :key="i" :field="field" v-model="formData[field.key]"/>
          </template>

          <div class="d-flex flex-row align-items-center">
            <b-button v-if="state === 'SAVING'" :disabled="true" variant="primary">Saving..</b-button>
            <b-button v-else type="submit" variant="primary">Save</b-button>

            <b-button v-if="testEmail" class="ml-2" variant="info" @click.prevent="sendTestEmail">Send Test Email</b-button>
            <b-button v-if="testEmail" class="ml-2" variant="info" @click.prevent="previewEmail">Preview</b-button>

            <span v-if="state === 'SAVED'" class="ml-2 text-success"><font-awesome-icon icon="check"/> Saved</span>
            <span v-if="state === 'EMAIL_SENDING'" class="ml-2">Sending...</span>
            <span v-if="state === 'EMAIL_SENT'" class="ml-2 text-success"><font-awesome-icon icon="check"/> Sent</span>
            <span v-if="state === 'ERRORED'" class="ml-2 text-danger"><font-awesome-icon icon="times"/> Errored</span>
          </div>

          <b-alert v-if="err" show variant="danger" class="mt-3">
            <p><pre>{{ err }}</pre></p>
          </b-alert>

          <b-modal v-if="testEmail" body-class="p-0 text-center" :id="`previewModal${id}`" hide-footer size="lg">
            <template v-slot:modal-header="{ close }">
              <h5 class="modal-title">Email Preview</h5>
              <div class="flex-fill"/>
              <button class="close d-none d-sm-inline-block" @click="() => previewType = 'Desktop'" type="button" aria-label="Preview Desktop">
                <font-awesome-icon icon="desktop"/>
              </button>
              <button class="close d-none d-sm-inline-block" @click="() => previewType = 'Mobile'" type="button" aria-label="Preview Mobile">
                <font-awesome-icon icon="mobile-alt"/>
              </button>
              <button class="close" @click="close" type="button" aria-label="Close">×</button>
            </template>

            <loading-spinner v-if="previewState === 'LOADING'" class="m-5"/>
            <b-alert v-else-if="previewState === 'ERROR'" class="m-5" show variant="danger">
              An error occurred rendering your email preview. Please close this popup &amp; try again.<br/>
              If this error persists please notify us at <a href="mailto:support@reallinks.io">support@reallinks.io</a>.
            </b-alert>
            <iframe
              :width="previewType === 'Desktop' ? '100%' : '340px'"
              height="500px"
              v-else-if="emailHTML"
              :srcdoc="emailHTML"
              class="border-0 mx-auto"
            ></iframe>
          </b-modal>

          <slot name="after-form"/>

        </b-form>
      </slot>
    </transition>
    <b-modal v-if="testEmail" body-class="cronDetailsModal" :id="`cronDetailsModal${id}`" title="Cron Details" size="lg">
      <b-row>
        <b-col class="py-3" cols="12">
          <pre v-if="cronDetails">{{cronDetails}}</pre>
          <span v-else>No cron is defined.</span>
        </b-col>
      </b-row>
      <template v-slot:modal-footer>
        <b-button class="mt-3" @click="$bvModal.hide(`cronDetailsModal${id}`)">Back</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import AssetBlock from '@/components/settings/AssetBlock';
import InputBlock from '@/components/settings/InputBlock';
import ScoreBlock from '@/components/settings/ScoreBlock';
import OnboardingBlock from '@/components/settings/OnboardingBlock';
import DashboardBlock from '@/components/settings/DashboardBlock';
import TemplateBlock from '@/components/settings/TemplateBlock';
import SendEmailMutation from '@/graphql/mutations/SendEmail.gql';
import TiptapEditor from '@/components/misc/TiptapEditor';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import { mapRoot } from '@/mixins/root';

const ALWAYS_ACTIVE = [
  'reengagement/linkedin-ready-1',
  'users/invite-1',
  'rewards/claimed-status-changed',
];

const renderEmailMutation = gql`
  mutation RenderEmail($template: String!,$data: String) {
    renderEmail(template: $template, data: $data)
  }
`;

export default {
  props: {
    title: {
      type: String,
      default: () => 'Title',
    },
    description: {
      type: String,
      default: () => 'Write a useful description here!',
    },
    icon: {
      type: [ String, Array ],
      default: () => null,
    },
    state: {
      type: String,
      default: () => null,
    },
    err: {
      type: [ Error, Object, String ],
      default: () => null,
    },
    testEmail: {
      type: Function,
      default: null,
    },
    testDataQuery: {
      type: String,
      default: null,
    },
    mockData: {
      type: Function,
      default: null,
    },
    fields: {
      type: Array,
      default: () => [],
    },
    tabs: {
      type: Array,
      default: () => null,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    company: {
      type: Object,
      default: () => null,
    },
    id: {
      type: String
    }
  },
  components: {
    AssetBlock,
    InputBlock,
    TemplateBlock,
    OnboardingBlock,
    DashboardBlock,
    ScoreBlock,
    TiptapEditor,
    LoadingSpinner,
  },
  data: () => ({
    formData: {},
    visible: false,
    previewType: 'Desktop',
    previewState: null,
    emailHTML: '',
    usePlainHTML: false,
    cronDetails: null,
  }),
  created() {
    this.onFormReset();
  },
  computed: {
    ...mapRoot([ 'user' ]),
    tabbedFields() {
      const { fields, tabs } = this;
      if (fields && tabs && tabs.length) {
        const output = fields.reduce((out, el) => {
          if (out.hasOwnProperty(el.tab || 'none')) {
            out[el.tab || 'none'].push(el);
          } else {
            out[el.tab || 'none'] = [ el ];
          }
          return out;
        }, {});
        console.log('TABBED FIELDS: ', output);
        return output;
      }
      return null;
    },
    isCronActive() {
      if (this.testEmail && this.company && Array.isArray(this.company.crons) && this.company.crons.length) {
        const { template } = this.testEmail(this.formData) || {};
        const { crons } = this.company;

        if (ALWAYS_ACTIVE.includes(template)) {
          // Some emails are sent from the platform rather than CRONs
          return true;
        } else {
          // Find the cron by email template
          const cron = crons.find(c => c && c.function.startsWith('emails-') && c.payload && c.payload.template === template);
          return cron && cron.status === 'ACTIVE';
        }
      } else {
        return false;
      }
    }
  },
  methods: {
    onFormReset() {
      this.formData = Object.assign({}, this.value);
    },
    onFormSubmit() {
      const formData = JSON.parse(JSON.stringify(this.formData));

      this.fields.forEach(field => {
        if (field.type === 'asset') {
          if (formData[field.key]) {
            const { id: assetID } = formData[field.key] || {};
            if (this.value[field.key] && this.value[field.key].id === assetID) {
              // Don't bother re-saving this
              delete formData[field.key];
            } else {
              formData[field.key] = { id: assetID };
            }
          }
        } else if ([ 'string', 'string!', 'string[]', 'multiline-string' ].includes(field.type)) {
          if (!formData[field.key]) {
            formData[field.key] = null;
          }
        }
      });

      this.$emit('input', formData);
    },
    async sendTestEmail() {
      try {
        this.$emit('updateState', 'EMAIL_SENDING');
        this.$assert(typeof this.testEmail === 'function', new Error('Failed to find a testEmail function'));
        const email = this.testEmail(this.formData);
        email.where = { users: { id: this.user.id } };

        this.$log.debug({ email });
        email.data = email.data ? JSON.stringify(email.data) : null;
        email.variables = email.variables ? JSON.stringify(email.variables) : null;

        await this.$apollo.mutate({
          mutation: SendEmailMutation,
          variables: { email },
        });
        this.$emit('updateState', 'EMAIL_SENT');
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to send a test email' });
      }
    },
    async previewEmail() {
      this.emailHTML = '';
      this.previewState = 'LOADING';
      this.$bvModal.show(`previewModal${this.id}`);

      try {
        this.$assert(typeof this.testEmail === 'function', new Error('Failed to find a testEmail function'));
        // @TODO: Add loading state
        const { template } = this.testEmail(this.formData);
        const { data: emailData } = await this.$apollo.query({
          query: gql`${this.testDataQuery}`,
          fetchPolicy: 'network-only',
        });
        // console.log('TEMPLATE: ', template);
        console.log('EMAIL DATA: ', this.mockData ? this.mockData(emailData) : emailData);
        const { data: { renderEmail } } = await this.$apollo.mutate({
          mutation: renderEmailMutation,
          variables: {
            template,
            data: JSON.stringify(this.mockData ? this.mockData(emailData) : emailData)
          },
        });
        const { body } = renderEmail;
        this.emailHTML = body;
        this.previewState = null;
      } catch (err) {
        this.$log.error(err);
        this.previewState = 'ERROR';
      }
    },
    showCronDetails() {
      const { company, testEmail, formData } = this;
      if (!testEmail || !company || !Array.isArray(company.crons)) {
        return;
      }
      const email = testEmail(formData);
      this.cronDetails = JSON.stringify(company.crons.find(({ payload }) => (payload.template === email.template)), null, 2);
      this.$bvModal.show(`cronDetailsModal${this.id}`);
    }
  }
};
</script>

<style  lang="scss">
.settings-form-block {
  .cron-on-switch, .cron-off-switch {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 7px;
    margin-right: 7px;
  }

  .cron-on-switch {
    background: #10ac84;
  }
  .cron-off-switch {
    background: #ee5253;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/settings/FormBlock.vue